import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarCheck, Users, Search, ArrowUpRight, CheckCircle2, X, ChevronRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { PriceBreakdown, PaymentSchedule, money } from "@/components/PriceBreakdown";
import { PhotoGallery, orderedPhotos, mediaSrc } from "@/components/PhotoGallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inp = "w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] h-11 px-3 text-[#1C1917] text-sm";
const btn = "inline-flex items-center justify-center gap-2 rounded-full bg-[#1C1917] text-white px-6 py-3 hover:bg-[#44403C] transition-colors duration-300 font-medium text-sm disabled:opacity-60";
const ghost = "inline-flex items-center justify-center gap-2 rounded-full border border-[#E7E5E4] bg-white text-[#1C1917] px-5 py-2.5 hover:bg-[#F5F5F4] transition-colors duration-300 font-medium text-sm";

const MIN_NIGHTS = 30;
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const nightsBetween = (a, b) => Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000);
const LENGTHS = [1, 2, 3, 6];

function TestingBanner() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    axios.get(`${API}/booking/mode`).then(({ data }) => setOn(Boolean(data?.guest_testing_mode))).catch(() => setOn(false));
  }, []);
  if (!on) return null;
  return (
    <div data-testid="testing-mode-banner" className="mt-5 max-w-3xl rounded-xl border border-[#FDE68A] bg-[#FEF3C7] px-4 py-3 text-sm text-[#92400E]">
      Preview / testing mode: you can explore pricing and send a test request, but no reservations are created and no payments are taken.
    </div>
  );
}

export default function Book() {
  const today = new Date().toISOString().slice(0, 10);
  const [checkIn, setCheckInRaw] = useState(today);
  const [checkOut, setCheckOut] = useState(addDays(today, MIN_NIGHTS));
  const setCheckIn = (v) => {
    setCheckInRaw(v);
    if (v && (!checkOut || nightsBetween(v, checkOut) < MIN_NIGHTS)) setCheckOut(addDays(v, MIN_NIGHTS));
  };
  const stayNights = checkIn && checkOut ? nightsBetween(checkIn, checkOut) : 0;
  const [guests, setGuests] = useState(1);
  const [type, setType] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    if (!checkIn || !checkOut) { toast.error("Choose check-in and check-out dates."); return; }
    if (new Date(checkOut) <= new Date(checkIn)) { toast.error("Check-out must be after check-in."); return; }
    if (stayNights < MIN_NIGHTS) { toast.error(`Direct bookings require a minimum stay of ${MIN_NIGHTS} consecutive nights.`); return; }
    setSearching(true);
    try {
      const params = { check_in: checkIn, check_out: checkOut, guests };
      if (type) params.type = type;
      const { data } = await axios.get(`${API}/booking/search`, { params });
      setResults(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed. Please try different dates.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div data-testid="book-page" className="pt-28 md:pt-32">
      {/* Hero + search */}
      <section className="px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <span data-testid="monthly-policy-badge" className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Monthly Furnished Stays — Minimum 30 Nights</span>
            <TestingBanner />
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.02] text-[#1C1917] max-w-3xl">
              Reserve your monthly stay with Masaya
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg text-[#44403C] leading-relaxed font-light max-w-2xl">
              Direct reservations are for stays of 30 consecutive nights or longer, priced by the month.
              See your full price, today's payment and your monthly schedule before you request. Our team
              confirms every request within one business day.
            </p>
          </Reveal>

          {/* Search bar */}
          <Reveal delay={0.3}>
            <form onSubmit={search} data-testid="booking-search-form" className="mt-10 rounded-2xl border border-[#E7E5E4] bg-white p-5 md:p-6 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div className="md:col-span-1">
                  <label className="text-xs text-[#78716C] block mb-1">Check-in</label>
                  <input type="date" min={today} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} data-testid="search-check-in" className={inp} />
                </div>
                <div className="md:col-span-1">
                  <label className="text-xs text-[#78716C] block mb-1">Check-out</label>
                  <input type="date" min={checkIn ? addDays(checkIn, MIN_NIGHTS) : today} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} data-testid="search-check-out" className={inp} />
                </div>
                <div className="md:col-span-1">
                  <label className="text-xs text-[#78716C] block mb-1">Guests</label>
                  <input type="number" min={1} max={20} value={guests} onChange={(e) => setGuests(Number(e.target.value) || 1)} data-testid="search-guests" className={inp} />
                </div>
                <div className="md:col-span-1">
                  <label className="text-xs text-[#78716C] block mb-1">Type</label>
                  <select value={type} onChange={(e) => setType(e.target.value)} data-testid="search-type" className={inp}>
                    <option value="">Any</option>
                    <option value="furnished_room">Furnished rooms</option>
                    <option value="luxury_home">Vacation homes</option>
                  </select>
                </div>
                <button type="submit" disabled={searching || stayNights < MIN_NIGHTS} data-testid="search-submit" className={btn + " h-11"}>
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
                </button>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-xs text-[#78716C] mr-1">Stay length:</span>
                {LENGTHS.map((m) => (
                  <button type="button" key={m} onClick={() => checkIn && setCheckOut(addDays(checkIn, m * 30))} data-testid={`length-${m}m`}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${stayNights === m * 30 ? "bg-[#1C1917] text-white border-[#1C1917]" : "border-[#E7E5E4] text-[#44403C] hover:bg-[#F5F5F4]"}`}>
                    {m} month{m === 1 ? "" : "s"}
                  </button>
                ))}
                <span data-testid="search-nights" className={`text-xs ml-auto ${stayNights < MIN_NIGHTS ? "text-red-700" : "text-[#44403C]"}`}>
                  {stayNights} night{stayNights === 1 ? "" : "s"}{stayNights < MIN_NIGHTS ? ` · minimum ${MIN_NIGHTS}` : ""}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-[#78716C]">
                <ShieldCheck className="w-3.5 h-3.5 text-[#8A9A86]" strokeWidth={1.75} />
                Request to Book only. Our team confirms availability before any payment is collected.
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {/* Results */}
      <section className="px-6 md:px-12 pt-10 pb-24 md:pb-32">
        <div className="max-w-6xl mx-auto">
          {results === null && (
            <p className="text-sm text-[#78716C] font-light">Choose your dates and search to see availability.</p>
          )}
          {results && (
            <>
              <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
                <p className="text-sm text-[#44403C]">
                  <span className="text-[#1C1917] font-medium">{results.results.length}</span>{" "}
                  {results.results.length === 1 ? "room" : "rooms"} available · {results.nights} night{results.nights === 1 ? "" : "s"}
                </p>
                <p className="text-xs text-[#78716C]">Estimates assume the permanent-resident tax exemption for 30+ night stays (pending verification) and include cleaning and management fees.</p>
              </div>
              {results.results.length === 0 && (
                <div className="rounded-2xl border border-dashed border-[#E7E5E4] bg-[#FAFAF9] p-10 text-center">
                  <p className="text-[#1C1917]">No rooms match these dates and guest count.</p>
                  <p className="mt-2 text-sm text-[#78716C] font-light">Try adjusting your dates, lowering guest count, or <a href="mailto:hello@masayahospitality.com" className="underline underline-offset-4">email us directly</a>.</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.results.map((r) => (
                  <div key={r.id} data-testid={`result-${r.slug}`} className="rounded-2xl border border-[#E7E5E4] bg-white p-5 flex flex-col">
                    <CoverPhoto room={r} />
                    <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#8A9A86]">{r.tier}</p>
                    <h3 className="mt-2 text-lg font-normal tracking-tight text-[#1C1917]">{r.name}</h3>
                    <p className="mt-1 text-xs text-[#78716C]">{r.property?.name} · {r.property?.city}</p>
                    <div className="mt-5">
                      <p className="text-xs text-[#78716C]">Monthly rate</p>
                      <p data-testid={`result-monthly-${r.slug}`} className="text-2xl font-light tracking-tight text-[#1C1917]">{money(r.monthly_rate)}<span className="text-sm text-[#78716C]"> / month</span></p>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-[#78716C]">Est. total · pending verification</p>
                        <p data-testid={`result-total-${r.slug}`} className="text-[#1C1917]">{money(r.estimated_total)}</p>
                        {r.estimate?.taxable_total !== undefined && (
                          <p data-testid={`result-taxable-${r.slug}`} className="text-[11px] text-[#78716C]">If not eligible: {money(r.estimate.taxable_total)}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#78716C]">Due at booking</p>
                        <p data-testid={`result-due-${r.slug}`} className="text-[#1C1917]">{money(r.estimate?.amount_due_today)}</p>
                      </div>
                    </div>
                    <p className="mt-2 text-[11px] text-[#78716C]">{r.max_guests} guest{r.max_guests === 1 ? "" : "s"} max · Minimum {Math.max(MIN_NIGHTS, r.min_nights || 0)} nights</p>
                    <button onClick={() => setSelectedRoom(r)} data-testid={`select-${r.slug}`} className={ghost + " mt-5 w-full"}>
                      Review & request <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <AnimatePresence>
        {selectedRoom && (
          <ReservationDrawer
            room={selectedRoom}
            checkIn={checkIn}
            checkOut={checkOut}
            guests={guests}
            nights={results?.nights || 0}
            onClose={() => setSelectedRoom(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ReservationDrawer({ room, checkIn, checkOut, guests, nights, onClose }) {
  const [f, setF] = useState({ guest_name: "", guest_email: "", guest_phone: "", message: "", num_guests: guests });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/booking/reservations`, {
        room_id: room.id,
        check_in: checkIn,
        check_out: checkOut,
        num_guests: f.num_guests,
        guest_name: f.guest_name,
        guest_email: f.guest_email,
        guest_phone: f.guest_phone,
        message: f.message,
      });
      setResult(data);
      setDone(data?.is_test ? "test" : true);
      toast.success(data?.is_test ? "Test request received (guest-testing mode)." : "Request submitted. We'll review and confirm availability.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-[#1C1917]/40 z-40" onClick={onClose} />
      <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.3 }}
        className="fixed top-0 right-0 h-full w-full sm:w-[520px] max-w-full bg-white z-50 shadow-2xl overflow-y-auto"
        data-testid="reservation-drawer">
        <div className="sticky top-0 bg-white border-b border-[#E7E5E4] px-6 py-4 flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] font-medium">Request to Book</p>
            <h2 className="mt-1 text-xl font-normal tracking-tight text-[#1C1917]">{room.name}</h2>
            <PhotoGallery photos={room.photos} alt={room.name} />
            <p className="text-xs text-[#78716C] mt-0.5">{room.tier} · {room.property?.name}</p>
          </div>
          <button onClick={onClose} data-testid="drawer-close" aria-label="Close" className="p-2 -mr-2 text-[#44403C] hover:text-[#1C1917]">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="px-6 py-6">
          {done ? (
            <BookingConfirmation result={result} test={done === "test"} room={room} checkIn={checkIn} checkOut={checkOut} nights={nights} onClose={onClose} />
          ) : (
            <>
              <div className="rounded-xl bg-[#F5F5F4] p-4 text-sm space-y-1 mb-5">
                <div className="flex justify-between"><span className="text-[#78716C]">Check-in</span><span className="text-[#1C1917]">{checkIn}</span></div>
                <div className="flex justify-between"><span className="text-[#78716C]">Check-out</span><span className="text-[#1C1917]">{checkOut}</span></div>
                <div className="flex justify-between"><span className="text-[#78716C]">Nights</span><span className="text-[#1C1917]">{nights}</span></div>
              </div>
              {room.estimate && (
                <div className="rounded-xl border border-[#E7E5E4] p-4 mb-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] font-medium mb-3">Estimated price</p>
                  <PriceBreakdown bd={room.estimate} testidPrefix="estimate" totalLabel="Total reservation amount" />
                  {(room.estimate.schedule || []).length > 1 && (
                    <div className="mt-4">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] font-medium mb-1">Payment schedule</p>
                      <PaymentSchedule rows={room.estimate.schedule} testidPrefix="estimate-schedule" />
                    </div>
                  )}
                  <p className="mt-3 text-xs text-[#78716C]">Next steps: sign the monthly stay agreement (including your written commitment to stay at least 30 consecutive nights), then Masaya verifies your exemption before sending a secure Stripe payment link.</p>
                </div>
              )}
              <form onSubmit={submit} className="space-y-3" data-testid="reservation-form">
                <Labeled label="Full legal name"><input required value={f.guest_name} onChange={(e) => set("guest_name", e.target.value)} data-testid="res-name" className={inp} /></Labeled>
                <Labeled label="Email"><input required type="email" value={f.guest_email} onChange={(e) => set("guest_email", e.target.value)} data-testid="res-email" className={inp} /></Labeled>
                <Labeled label="Phone"><input required value={f.guest_phone} onChange={(e) => set("guest_phone", e.target.value)} data-testid="res-phone" className={inp} placeholder="+1 512 555 0100" /></Labeled>
                <Labeled label="Guests">
                  <input type="number" min={1} max={room.max_guests} value={f.num_guests} onChange={(e) => set("num_guests", Number(e.target.value) || 1)} data-testid="res-guests" className={inp} />
                </Labeled>
                <Labeled label="Notes / special requests">
                  <textarea rows={3} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="res-message"
                    className="w-full rounded-lg bg-white border border-[#E7E5E4] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] focus:border-[#8A9A86] px-3 py-2 text-[#1C1917] text-sm" placeholder="Travel purpose, arrival time, anything else…" />
                </Labeled>
                <p className="text-xs text-[#78716C] pt-1" data-testid="res-account-note">
                  Your Guest Portal access is created with this request: we email a secure one-time sign-in link to verify your address (no password needed — you can set one later). Already a Masaya guest? Use the same email and we'll link this request to your existing account.
                </p>
                <p className="text-xs text-[#78716C]">
                  No payment is required at this step. A Masaya staff member will confirm availability and send a secure payment link once approved.
                </p>
                <button type="submit" disabled={busy} data-testid="res-submit" className={btn + " w-full"}>
                  {busy ? "Submitting…" : (<>Request to Book <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} /></>)}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.aside>
    </>
  );
}

function CoverPhoto({ room }) {
  const photos = orderedPhotos(room.photos);
  const cover = photos[0];
  return (
    <div className="-mx-5 -mt-5 mb-4 aspect-[4/3] overflow-hidden rounded-t-2xl bg-[#F5F5F4]" data-testid={`result-photo-${room.slug}`}>
      {cover ? (
        <img src={mediaSrc(cover.url, 960)} srcSet={`${mediaSrc(cover.url, 480)} 480w, ${mediaSrc(cover.url, 960)} 960w, ${mediaSrc(cover.url, 1600)} 1600w`}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" alt={cover.caption || room.name} loading="lazy" decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-[1.03]" />
      ) : (
        <div className="w-full h-full flex items-end p-4 bg-gradient-to-br from-[#E7E5E4] to-[#D6D3D1]" data-testid={`result-photo-placeholder-${room.slug}`}>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#57534E]">Photos coming soon</span>
        </div>
      )}
      {photos.length > 1 && <span className="relative -top-8 left-3 rounded-full bg-black/55 text-white text-[11px] px-2 py-0.5" data-testid={`result-photo-count-${room.slug}`}>{photos.length} photos</span>}
    </div>
  );
}

function BookingConfirmation({ result = {}, test, room, checkIn, checkOut, nights, onClose }) {
  const rows = [["Reservation reference", result.reference || "—"], ["Property", `${result.property_name || ""}${result.property_name ? " · " : ""}${result.room_name || room.name}`],
    ["Requested dates", `${checkIn} → ${checkOut} (${nights} nights)`], ["Estimated total", money(result.estimated_total ?? room.estimate?.total)], ["Booking status", "Pending Review"]];
  return (
    <div className="py-4" data-testid="reservation-success">
      <div className="text-center">
        <CheckCircle2 className="w-10 h-10 mx-auto text-[#8A9A86]" strokeWidth={1.5} />
        <p className="mt-5 text-xl tracking-tight font-normal text-[#1C1917]">Thank you! Your booking request has been received.</p>
        <p className="mt-2 text-sm text-[#44403C] font-light max-w-md mx-auto" data-testid="reservation-success-text">
          {test ? "Masaya is in guest-testing mode: this is a test request, no room is held and no payment will be requested. " : ""}
          Submitting a request does <strong>not</strong> mean the reservation is confirmed — Masaya reviews availability manually and will email you.
        </p>
      </div>
      <div data-testid="booking-confirmation" className="mt-6 rounded-xl border border-[#E7E5E4] p-4 text-sm space-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3"><span className="text-[#78716C]">{k}</span><span data-testid={`confirmation-${k.toLowerCase().replace(/\s+/g, "-")}`} className={`text-[#1C1917] text-right ${k === "Reservation reference" ? "font-mono" : ""}`}>{v}</span></div>
        ))}
      </div>
      <div className="mt-5 rounded-xl bg-[#F5F5F4] p-4 text-sm">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] font-medium mb-2">What happens next</p>
        <ol className="list-decimal pl-5 space-y-1 text-[#44403C]" data-testid="confirmation-next-steps">
          <li>Check your inbox: we emailed a secure sign-in link to <strong>{result.guest_email}</strong>{result.account_created ? " — your Guest Portal account is ready, no password needed." : " for your existing Guest Portal account."}</li>
          <li>Masaya confirms availability (usually within one business day).</li>
          <li>Sign the monthly stay agreement (30 consecutive nights minimum) and verify your government ID.</li>
          <li>Masaya reviews tax-exemption eligibility, then sends your secure Stripe payment link. No charge until then.</li>
        </ol>
      </div>
      <a href="/guest" data-testid="go-to-guest-portal" className={btn + " w-full mt-5"}>Go to My Guest Portal <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} /></a>
      <button onClick={onClose} className="mt-4 w-full text-sm text-[#8A9A86] underline underline-offset-4">Close</button>
    </div>
  );
}

function RequestSummary({ room, checkIn, checkOut, nights }) {
  const est = room.estimate;
  if (!est) return null;
  const rows = [["Room", room.name], ["Dates", `${checkIn} → ${checkOut}`], ["Nights", nights],
    ["Texas Tax", money(est.texas_tax)], ["Occupancy Tax", money(est.occupancy_tax)], ["Estimated total", money(est.total)]];
  return (
    <div data-testid="request-summary" className="mt-6 text-left rounded-xl border border-[#E7E5E4] p-4 text-sm space-y-1">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] font-medium">Request summary</p>
        {est.tax_status === "pending_verification" && <span data-testid="request-summary-pending" className="rounded-full bg-[#FEF3C7] text-[#92400E] px-2.5 py-0.5 text-xs font-medium">Pending verification</span>}
      </div>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3"><span className="text-[#78716C]">{k}</span><span data-testid={`request-summary-${k.toLowerCase().replace(/\s+/g, "-")}`} className="text-[#1C1917]">{v}</span></div>
      ))}
      {est.taxable_total !== undefined && <p className="pt-1 text-xs text-[#78716C] text-right">If not eligible: {money(est.taxable_total)} incl. taxes</p>}
    </div>
  );
}

function Labeled({ label, children }) {
  return <div><label className="text-xs text-[#78716C] block mb-1">{label}</label>{children}</div>;
}
